import React, { useEffect, Suspense, useMemo } from 'react'
import { Outlet, Route, Routes, useNavigate } from 'react-router-dom'
import { collection, query, where, getDocs } from 'firebase/firestore'
import { toast } from 'react-toastify'
import { ToastContainer } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'

// Import components
import { Navbar } from './components'
import LoadingSpinner, {
  SkeletonDashboard,
} from './components/Performance/LoadingSpinner'
import ErrorBoundary, {
  DashboardErrorBoundary,
} from './components/ErrorBoundary'

// Import utilities
import { firestore } from './utils/firebase'
import {
  passwordlessSignin,
  setNavigationFunction,
  AuthUserProvider,
  useAuth,
} from './utils/firebase/auth'
import { generateDashboardRoutes } from './utils/permissions/RbacRoutes'
import { FeatureFlagProvider } from './utils/permissions/FeatureFlags'
import { RoleProvider } from './utils/permissions/RoleContext'
import { ErrorProvider } from './utils/errors/ErrorContext'
import { usePerformanceMonitor } from './utils/performance/monitoring'
import {
  LazyHomePage,
  LazyAboutUs,
  LazyServices,
  LazyTeam,
  LazyLibrary,
  LazyGalleryPage,
  LazyLoginPage,
  LazySignUpPage,
  LazyKAI,
  LazyChatPage,
  LazyDonations,
  LazyResourceDetail,
  LazyNotFound404Page,
  LazyRbacTest,
  LazyFeatureFlagTest,
  LazyDevTools,
  LazyPaymentTest,
  LazyCompetitionTest,
  preloadCriticalComponents,
} from './utils/performance/lazyComponents'

// Import styles
import './app.scss'

// Lazy load remaining components
const LazyRouteRegistryTest = React.lazy(() =>
  import('./pages/site/RouteRegistryTest')
)
const LazyNavRegistryTest = React.lazy(() =>
  import('./pages/site/NavRegistryTest')
)
const LazyPermissionsExplorer = React.lazy(() =>
  import('./pages/site/PermissionsExplorer')
)
const LazyTermsOfService = React.lazy(() =>
  import('./pages/site/TermsOfService')
)
const LazyPrivacy = React.lazy(() => import('./pages/site/Privacy'))
const LazyReports = React.lazy(() => import('./pages/site/Reports'))

/**
 * List of public routes that don't require authentication
 * These routes are accessible to all users whether logged in or not
 * Any route not in this list will trigger auth checks and potential redirects
 * @constant
 */
const publicRoutes = [
  '/',
  '/about',
  '/services',
  '/team',
  '/library',
  '/k-ai',
  '/testimonials',
  '/donations',
  '/tos',
  '/privacy-policy',
  '/gallery',
  '/reports',
  '/donate',
  '/resource',
  '/account/login',
  '/account/signup',
]

/**
 * Helper function to check if a path is public
 * Used to determine whether authentication checks should be performed
 * @param {string} path - The current route path
 * @returns {boolean} - True if the path is public, false otherwise
 */
const isPublicRoute = (path) => {
  return publicRoutes.some((route) => path.startsWith(route))
}

/**
 * Main App component - Application Entry Point
 *
 * This component serves as the central router and provider container for the entire application.
 * It wraps all routes with necessary context providers:
 * - AuthUserProvider: Handles Firebase authentication state
 * - RoleProvider: Manages user roles and permissions
 * - FeatureFlagProvider: Controls feature availability
 *
 * The component also handles:
 * 1. Authentication state management and redirects
 * 2. Profile completion verification
 * 3. Route protection based on user roles
 * 4. Central routing for the entire application
 *
 * @returns {JSX.Element} The rendered application
 */
const App = () => {
  const navigate = useNavigate() // Hook to programmatically navigate
  const { authUser } = useAuth() // Use the auth hook at the top level

  // Initialize performance monitoring
  usePerformanceMonitor()

  /**
   * Effect hook for handling navigation and passwordless authentication
   * Sets up the navigation function for auth utilities and handles email link authentication
   */
  useEffect(() => {
    setNavigationFunction(navigate)

    // Preload critical components for better performance
    preloadCriticalComponents()

    /**
     * Attempts passwordless sign-in when the app loads with an email sign-in link
     * Updates authentication state if successful
     */
    const checkAuth = async () => {
      try {
        // Try to sign in with email link (passwordless)
        const result = await passwordlessSignin()
        if (result) {
          console.log('Signed in with passwordless auth')
        }
      } catch (error) {
        console.error('Error in auth check:', error)
      }
    }

    // Check if the current URL is a sign-in link
    if (window.location.href.includes('apiKey=')) {
      checkAuth()
    }
  }, [navigate])

  /**
   * Effect hook for profile completion verification
   * Redirects users to complete their profile if required fields are missing
   * Only applies to protected routes that require authentication
   */
  useEffect(() => {
    const checkUserProfile = async () => {
      // Use the authUser from the hook at the component level
      if (authUser) {
        try {
          // Check if Firebase is available before making Firestore calls
          if (!firestore) {
            console.warn('Firebase not available - skipping profile check')
            return
          }

          // Check if the current path is a public route
          const currentPath = window.location.pathname
          if (isPublicRoute(currentPath)) {
            // Don't redirect for public routes
            return
          }

          // For authentication-required routes, check profile completion
          const userProfileRef = collection(firestore, 'userProfiles')
          const q = query(userProfileRef, where('uid', '==', authUser.uid))
          const querySnapshot = await getDocs(q)


          // If user has no profile document or has an incomplete profile, redirect to profile page
          if (
            querySnapshot.empty ||
            !querySnapshot.docs[0].data().firstName ||
            !querySnapshot.docs[0].data().lastName ||
            !querySnapshot.docs[0].data().educationLevel
          ) {
            // Only redirect if not already on the profile page
            if (
              !window.location.pathname.includes('/dashboard/profile')
            ) {
              navigate('/dashboard/profile')

              // Show a toast notification
              toast.info('Please complete your profile to continue', {
                position: 'top-center',
                autoClose: 5000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
              })
            }
          }
        } catch (error) {
          console.error('Error checking user profile:', error)
        }
      }
    }

    // Run the profile check
    checkUserProfile()
  }, [navigate, authUser]) // Removed pathname dependency

  /**
   * Layout component that adds Navbar to routes
   * Uses React Router's Outlet to render nested route components
   * This creates a consistent layout across main public routes
   *
   * @returns {JSX.Element} Layout with Navbar and nested route content
   */
  const WithNavBar = () => {
    return (
      <>
        <Navbar /> {/* Render the Navbar component */}
        <Outlet /> {/* Render the nested route components */}
      </>
    )
  }

  // Generate all dashboard routes using our centralized system - memoized to prevent infinite re-renders
  const dashboardRoutes = useMemo(() => {
    return generateDashboardRoutes()
  }, [])

  return (
    <ErrorProvider>
      <ErrorBoundary userId={authUser?.uid}>
        <AuthUserProvider>
          <RoleProvider>
            <FeatureFlagProvider>
              <div className="app">
                <Routes>
                  {/* Define routes for the application */}
                  <Route path="/" element={<WithNavBar />}>
                    <Route
                      index
                      element={
                        <Suspense
                          fallback={
                            <LoadingSpinner message="Loading home page..." />
                          }
                        >
                          <LazyHomePage />
                        </Suspense>
                      }
                    />
                    <Route
                      path="about"
                      element={
                        <Suspense
                          fallback={
                            <LoadingSpinner message="Loading about page..." />
                          }
                        >
                          <LazyAboutUs />
                        </Suspense>
                      }
                    />
                    <Route
                      path="services"
                      element={
                        <Suspense
                          fallback={
                            <LoadingSpinner message="Loading services..." />
                          }
                        >
                          <LazyServices />
                        </Suspense>
                      }
                    />
                    <Route
                      path="team"
                      element={
                        <Suspense
                          fallback={
                            <LoadingSpinner message="Loading team page..." />
                          }
                        >
                          <LazyTeam />
                        </Suspense>
                      }
                    />
                    <Route
                      path="library"
                      element={
                        <Suspense
                          fallback={
                            <LoadingSpinner message="Loading library..." />
                          }
                        >
                          <LazyLibrary />
                        </Suspense>
                      }
                    />
                    <Route
                      path="k-ai"
                      element={
                        <Suspense
                          fallback={
                            <LoadingSpinner message="Loading AI assistant..." />
                          }
                        >
                          <LazyKAI />
                        </Suspense>
                      }
                    />
                    <Route
                      path="chat/:agentSlug"
                      element={
                        <Suspense
                          fallback={
                            <LoadingSpinner message="Loading chat..." />
                          }
                        >
                          <LazyChatPage />
                        </Suspense>
                      }
                    />
                    <Route path="testimonials" />
                    <Route
                      path="donations"
                      element={
                        <Suspense
                          fallback={
                            <LoadingSpinner message="Loading donations..." />
                          }
                        >
                          <LazyDonations />
                        </Suspense>
                      }
                    />
                    <Route
                      path="tos"
                      element={
                        <Suspense
                          fallback={
                            <LoadingSpinner message="Loading terms..." />
                          }
                        >
                          <LazyTermsOfService />
                        </Suspense>
                      }
                    />
                    <Route
                      path="privacy-policy"
                      element={
                        <Suspense
                          fallback={
                            <LoadingSpinner message="Loading privacy policy..." />
                          }
                        >
                          <LazyPrivacy />
                        </Suspense>
                      }
                    />
                    <Route
                      path="gallery"
                      element={
                        <Suspense
                          fallback={
                            <LoadingSpinner message="Loading gallery..." />
                          }
                        >
                          <LazyGalleryPage />
                        </Suspense>
                      }
                    />
                    <Route
                      path="reports"
                      element={
                        <Suspense
                          fallback={
                            <LoadingSpinner message="Loading reports..." />
                          }
                        >
                          <LazyReports />
                        </Suspense>
                      }
                    />
                    <Route
                      path="donate"
                      element={
                        <Suspense
                          fallback={
                            <LoadingSpinner message="Loading donations..." />
                          }
                        >
                          <LazyDonations />
                        </Suspense>
                      }
                    />
                    {/* Development and testing routes - only available in development or for CORE_MEMBER */}
                    {(process.env.NODE_ENV === 'development' ||
                      process.env.REACT_APP_ENABLE_DEV_ROUTES === 'true') && (
                      <>
                        <Route
                          path="rbac-test"
                          element={
                            <Suspense
                              fallback={
                                <LoadingSpinner message="Loading RBAC test..." />
                              }
                            >
                              <LazyRbacTest />
                            </Suspense>
                          }
                        />
                        <Route
                          path="feature-flags"
                          element={
                            <Suspense
                              fallback={
                                <LoadingSpinner message="Loading feature flags..." />
                              }
                            >
                              <LazyFeatureFlagTest />
                            </Suspense>
                          }
                        />
                        <Route
                          path="route-registry"
                          element={
                            <Suspense
                              fallback={
                                <LoadingSpinner message="Loading route registry..." />
                              }
                            >
                              <LazyRouteRegistryTest />
                            </Suspense>
                          }
                        />
                        <Route
                          path="nav-registry"
                          element={
                            <Suspense
                              fallback={
                                <LoadingSpinner message="Loading nav registry..." />
                              }
                            >
                              <LazyNavRegistryTest />
                            </Suspense>
                          }
                        />
                        <Route
                          path="dev-tools"
                          element={
                            <Suspense
                              fallback={
                                <LoadingSpinner message="Loading dev tools..." />
                              }
                            >
                              <LazyDevTools />
                            </Suspense>
                          }
                        />
                        <Route
                          path="permissions-explorer"
                          element={
                            <Suspense
                              fallback={
                                <LoadingSpinner message="Loading permissions explorer..." />
                              }
                            >
                              <LazyPermissionsExplorer />
                            </Suspense>
                          }
                        />
                        <Route
                          path="testing/payment"
                          element={
                            <Suspense
                              fallback={
                                <LoadingSpinner message="Loading payment test..." />
                              }
                            >
                              <LazyPaymentTest />
                            </Suspense>
                          }
                        />
                        <Route
                          path="testing/competition"
                          element={
                            <Suspense
                              fallback={
                                <LoadingSpinner message="Loading competition test..." />
                              }
                            >
                              <LazyCompetitionTest />
                            </Suspense>
                          }
                        />
                      </>
                    )}
                    <Route
                      path="*"
                      element={
                        <Suspense
                          fallback={
                            <LoadingSpinner message="Loading error page..." />
                          }
                        >
                          <LazyNotFound404Page />
                        </Suspense>
                      }
                    />
                    <Route
                      path="resource/:id"
                      element={
                        <Suspense
                          fallback={
                            <LoadingSpinner message="Loading resource details..." />
                          }
                        >
                          <LazyResourceDetail />
                        </Suspense>
                      }
                    />
                  </Route>
                  <Route path="account">
                    <Route
                      index
                      element={
                        <Suspense
                          fallback={
                            <LoadingSpinner message="Loading error page..." />
                          }
                        >
                          <LazyNotFound404Page />
                        </Suspense>
                      }
                    />
                    <Route
                      path="signup"
                      element={
                        <Suspense
                          fallback={
                            <LoadingSpinner message="Loading signup..." />
                          }
                        >
                          <LazySignUpPage />
                        </Suspense>
                      }
                    />
                    <Route
                      path="login"
                      element={
                        <Suspense
                          fallback={
                            <LoadingSpinner message="Loading login..." />
                          }
                        >
                          <LazyLoginPage />
                        </Suspense>
                      }
                    />
                    <Route
                      path="*"
                      element={
                        <Suspense
                          fallback={
                            <LoadingSpinner message="Loading error page..." />
                          }
                        >
                          <LazyNotFound404Page />
                        </Suspense>
                      }
                    />
                  </Route>

                  {/* Include all dashboard routes generated from our centralized system */}
                  <Route
                    path="/students/dashboard/*"
                    element={
                      <DashboardErrorBoundary userId={authUser?.uid}>
                        <Suspense fallback={<SkeletonDashboard />}>
                          {dashboardRoutes.find(
                            (route) =>
                              route.props.path === '/students/dashboard/*'
                          )}
                        </Suspense>
                      </DashboardErrorBoundary>
                    }
                  />
                  <Route
                    path="/ambassadors/dashboard/*"
                    element={
                      <DashboardErrorBoundary userId={authUser?.uid}>
                        <Suspense fallback={<SkeletonDashboard />}>
                          {dashboardRoutes.find(
                            (route) =>
                              route.props.path === '/ambassadors/dashboard/*'
                          )}
                        </Suspense>
                      </DashboardErrorBoundary>
                    }
                  />
                  {/* Admin dashboard removed - only STUDENT and AMBASSADOR dashboards */}

                  {/* Fallback for other dashboard routes */}
                  {useMemo(() => {
                    const fallbackRoutes = dashboardRoutes.filter(
                      (route) =>
                        !route.props.path?.includes('/students/dashboard') &&
                        !route.props.path?.includes('/ambassadors/dashboard')
                    )
                    return fallbackRoutes.map((route, index) => (
                      <Route
                        key={index}
                        {...route.props}
                        element={
                          <Suspense fallback={<SkeletonDashboard />}>
                            {route.props.element}
                          </Suspense>
                        }
                      />
                    ))
                  }, [dashboardRoutes])}

                  <Route
                    path="*"
                    element={
                      <Suspense
                        fallback={
                          <LoadingSpinner message="Loading error page..." />
                        }
                      >
                        <LazyNotFound404Page />
                      </Suspense>
                    }
                  />
                </Routes>
                <ToastContainer
                  position="top-center"
                  autoClose={1000}
                  hideProgressBar={false}
                  newestOnTop={false}
                  closeOnClick
                  rtl={false}
                  pauseOnFocusLoss
                  draggable
                  pauseOnHover
                  style={{ width: 'min(90%, 512px)', margin: '1rem' }}
                  theme="light"
                />
              </div>
            </FeatureFlagProvider>
          </RoleProvider>
        </AuthUserProvider>
      </ErrorBoundary>
    </ErrorProvider>
  )
}

export default App
